// idle-state-tracker.service.ts
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subscription, fromEvent, merge, timer } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { LoggerService } from './logger.service';

/**
 * Service that tracks user idle state based on DOM events
 */
@Injectable({
    providedIn: 'root',
})
export class IdleStateTrackerService implements OnDestroy {
    private readonly defaultIdleTimeoutMs = 15000;
    private readonly isIdle$ = new BehaviorSubject<boolean>(false);
    private subscription: Subscription | null = null;

    constructor(private ngZone: NgZone, protected logger: LoggerService) {}

    /**
     * Start tracking idle state and return an observable
     */
    startTracking(idleTimeoutMs: number = this.defaultIdleTimeoutMs): Observable<boolean> {
        if (this.subscription) {
            this.logger.log('Tracking is already active');
            return this.isIdle$.asObservable().pipe(distinctUntilChanged());
        }

        this.ngZone.runOutsideAngular(() => {
            const passiveOptions: AddEventListenerOptions = { passive: true };

            // Rich text editors (e.g. TinyMCE) use a same-origin iframe; those events do not
            // bubble to the parent document, so we must listen on iframe documents too.
            const activityEvents$ = merge(
                this.userActivityFromDocument$(document, passiveOptions),
                fromEvent(window, 'focus'),
                fromEvent(document, 'visibilitychange').pipe(
                    map(() => (document.hidden ? 'hidden' : 'visible')),
                    tap((visibility) => {
                        if (visibility === 'hidden') {
                            this.setIdleState(true, 'tab hidden');
                        }
                    }),
                    map((visibility) => ({ visibility }))
                ),
                this.sameOriginIframeUserActivity$(passiveOptions)
            );

            const blurEvents$ = fromEvent(window, 'blur').pipe(tap(() => this.setIdleState(true, 'window blurred')));

            this.subscription = merge(activityEvents$, blurEvents$)
                .pipe(
                    startWith({ init: true }),
                    switchMap((evt) => {
                        if (
                            typeof evt === 'object' &&
                            evt !== null &&
                            'visibility' in evt &&
                            (evt as { visibility: string }).visibility === 'hidden'
                        ) {
                            return EMPTY;
                        }

                        this.setIdleState(false, 'activity');

                        return timer(idleTimeoutMs).pipe(
                            tap(() => this.setIdleState(true, `no activity for ${idleTimeoutMs}ms`))
                        );
                    })
                )
                .subscribe();
        });

        return this.isIdle$.asObservable().pipe(distinctUntilChanged());
    }

    /**
     * Check if user is currently idle
     */
    isIdle(): boolean {
        return this.isIdle$.getValue();
    }

    /**
     * Destroy tracking and cleanup
     */
    destroyTracking(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
        // Reset to initial state without completing the BehaviorSubject
        // This allows the service to be reused
        this.setIdleState(false, 'tracking destroyed');
        this.logger.log('Idle tracking destroyed and cleaned up');
    }

    ngOnDestroy(): void {
        this.destroyTracking();
    }

    private setIdleState(isIdle: boolean, reason: string): void {
        const currentValue = this.isIdle$.getValue();
        if (currentValue === isIdle) {
            return;
        }

        this.ngZone.run(() => this.isIdle$.next(isIdle));

        const stateLabel = isIdle ? 'idle' : 'active';
        this.logger.log(`User is now ${stateLabel} (${reason})`);
    }

    /** Pointer/keyboard/scroll activity on a single document (top window or iframe). */
    private userActivityFromDocument$(doc: Document, passiveOptions: AddEventListenerOptions): Observable<unknown> {
        return merge(
            fromEvent(doc, 'mousemove'),
            fromEvent(doc, 'mousedown'),
            fromEvent(doc, 'keydown'),
            fromEvent(doc, 'click'),
            fromEvent(doc, 'wheel', passiveOptions),
            fromEvent(doc, 'touchstart', passiveOptions),
            fromEvent(doc, 'touchmove', passiveOptions),
            fromEvent(doc, 'scroll', passiveOptions)
        );
    }

    /**
     * Hooks same-origin iframes (including ones inserted later, e.g. TinyMCE) so activity
     * inside the iframe resets idle state. Cross-origin iframes are skipped.
     */
    private sameOriginIframeUserActivity$(passiveOptions: AddEventListenerOptions): Observable<unknown> {
        return new Observable<unknown>((subscriber) => {
            const rootTeardown = new Subscription();
            const subsByIframe = new Map<HTMLIFrameElement, Subscription>();
            const wiredIframes = new WeakSet<HTMLIFrameElement>();

            const hookIframe = (iframe: HTMLIFrameElement) => {
                if (wiredIframes.has(iframe)) {
                    return;
                }
                wiredIframes.add(iframe);

                const onLoad = () => {
                    subsByIframe.get(iframe)?.unsubscribe();
                    try {
                        const idoc = iframe.contentDocument;
                        if (!idoc) {
                            return;
                        }
                        const sub = this.userActivityFromDocument$(idoc, passiveOptions).subscribe((ev) =>
                            subscriber.next(ev)
                        );
                        subsByIframe.set(iframe, sub);
                    } catch {
                        /* cross-origin */
                    }
                };
                iframe.addEventListener('load', onLoad);
                rootTeardown.add(() => iframe.removeEventListener('load', onLoad));
                onLoad();
            };

            document.querySelectorAll('iframe').forEach((el) => {
                if (el instanceof HTMLIFrameElement) {
                    hookIframe(el);
                }
            });

            const mo = new MutationObserver((mutations) => {
                for (const m of mutations) {
                    for (const n of Array.from(m.addedNodes)) {
                        if (n instanceof HTMLIFrameElement) {
                            hookIframe(n);
                        }
                        if (n instanceof Element) {
                            n.querySelectorAll('iframe').forEach((iframeEl) => {
                                if (iframeEl instanceof HTMLIFrameElement) {
                                    hookIframe(iframeEl);
                                }
                            });
                        }
                    }
                }
            });
            mo.observe(document.documentElement, { childList: true, subtree: true });
            rootTeardown.add(() => mo.disconnect());
            rootTeardown.add(() => {
                subsByIframe.forEach((s) => s.unsubscribe());
                subsByIframe.clear();
            });

            return () => {
                rootTeardown.unsubscribe();
            };
        });
    }
}
